import Axios, { AxiosInstance, AxiosResponse } from 'axios';
import {
  CustodianClient,
  PERMISSION_NAMES as CUSTODIAN_PERMISSION_NAMES,
  PERMISSIONS as CUSTODIAN_PERMISSIONS,
} from './clients/custodian';
import {
  DigitaltwinClient,
  PERMISSION_NAMES as DIGITALTWIN_PERMISSION_NAMES,
  PERMISSIONS as DIGITALTWIN_PERMISSIONS,
} from './clients/digitaltwin';
import { FileClient, PERMISSION_NAMES as FILE_PERMISSION_NAMES, PERMISSIONS as FILE_PERMISSIONS } from './clients/file';
import {
  IdentityClient,
  PERMISSION_NAMES as IDENTITY_PERMISSION_NAMES,
  PERMISSIONS as IDENTITY_PERMISSIONS,
} from './clients/identity';
import {
  OrganizationClient,
  PERMISSION_NAMES as ORGANIZATION_PERMISSION_NAMES,
  PERMISSIONS as ORGANIZATION_PERMISSIONS,
  PublicOrganizationSettings,
} from './clients/organization';
import {
  PERMISSION_NAMES as PRESENTATION_PERMISSION_NAMES,
  PERMISSIONS as PRESENTATION_PERMISSIONS,
  PresentationClient,
} from './clients/presentation';
import authInterceptor, { AccessTokenProvider, OnAccessTokenProviderError } from './clients/shared/interceptor';
import {
  PERMISSION_NAMES as TEMPLATE_PERMISSION_NAMES,
  PERMISSIONS as TEMPLATE_PERMISSIONS,
  TemplateClient,
} from './clients/template';
import { PERMISSIONS as TPL_ORDER_PERMISSIONS, TPL_ORDER_PERMISSION_NAMES, TPLOrderClient } from './clients/tpl-order';
import {
  PERMISSION_NAMES as VOUCHER_PERMISSION_NAMES,
  PERMISSIONS as VOUCHER_PERMISSIONS,
  VoucherClient,
} from './clients/voucher';
import {
  PERMISSION_NAMES as WARRANTY_PERMISSION_NAMES,
  PERMISSIONS as WARRANTY_PERMISSIONS,
  WarrantyClient,
} from './clients/warranty';
import { NotificationClient } from './clients/notification';
import { AuthClient } from './clients/auth';

export {
  NotificationClient,
  GetNotifications,
  ReadNotification,
  Notification,
  TransferCanceledDetail,
  TransferAcceptedDetail,
  TransferDeniedDetail,
  TransferInitiatedDetail,
} from './clients/notification';

export {
  MessageResponse,
  Certificate,
  CreateCertificate,
  CreateSpecification,
  UpdateCertificate,
  Specification,
  CertificatesResponse,
  GetAllCertificatesOptions,
  DigitalTwin,
  DigitaltwinClient,
  DigitalTwinEvent,
  CreateDigitaltwin,
  UpdateSpecification,
  OwnershipRequestAcceptedEvent,
  SoldEvent,
  OwnershipRequest,
  OWNERSHIP_REQUEST_STATUS,
  ProvenanceMap,
  CreateProvenanceMap,
  UpdateProvenanceMap,
  EventTypes,
  MappedEvent,
  Transfer,
  TransferType,
} from './clients/digitaltwin';
export {
  Custodian,
  CustodianClient,
  CreateCustodian,
  UpdateCustodian as UpdateCustodianInput,
} from './clients/custodian';
export { Presentation, CreatePresentation, UpdatePresentation } from './clients/presentation';
export {
  Identity,
  IdentityClient,
  UpdateIdentity,
  CreateIdentity,
  TokenResponse,
  User,
  ConnectionType,
  ConnectIdentity,
  IdentityType,
} from './clients/identity';
export { VoucherClient, Voucher, VoucherState, LinkedVoucher, GenericVoucher } from './clients/voucher';
export {
  TemplateClient,
  Template,
  CreateTemplateInput,
  CreateTemplateBatchInput,
  UpdateTemplateInput,
  BatchStatus,
  EntryStatus,
} from './clients/template';
export { FileInfo, Folder } from './clients/file';
export { Warranty, CreateWarranty, UpdateWarranty } from './clients/warranty';
export { TPLOrderClient, CreateTPLOrder, TPLOrder, UpdateTPLOrder } from './clients/tpl-order';
export {
  Organization,
  UpdateOrganizationSettings,
  OrganizationSettings,
  ThemeAppearance,
  SortDirection,
  IdentificationMethod,
  SoMe,
} from './clients/organization';
export {
  Field,
  Fields,
  FieldTypes,
  GalleryField,
  NumberField,
  AttributeField,
  ImageField,
  TextField,
  MappedEventField,
  VideoField,
} from './clients/shared/fields';
export { Image } from './clients/shared/image';

export default class TT {
  public static DigitaltwinClient: DigitaltwinClient;
  public static FileClient: FileClient;
  public static PresentationClient: PresentationClient;
  public static CustodianClient: CustodianClient;
  public static IdentityClient: IdentityClient;
  public static VoucherClient: VoucherClient;
  public static TemplateClient: TemplateClient;
  public static WarrantyClient: WarrantyClient;
  public static TPLOrderClient: TPLOrderClient;
  public static OrganizationClient: OrganizationClient;
  public static NotificationClient: NotificationClient;
  public static AuthClient: AuthClient;

  public static InitAuthClient = (options: { host: string; orgId: string }): AuthClient => {
    const { host, orgId } = options;
    if (!host) {
      throw new Error('ServiceHost should be defined');
    }

    if (!host.startsWith('https://api')) {
      throw new Error('ServiceHost must start with "https://api"');
    }
    const axiosInstance = Axios.create({
      headers: {
        'Content-Type': 'application/json',
        'x-organization-id': orgId,
      },
    });
    const authClient = new AuthClient({ axiosInstance, host, serviceName: 'user' });
    TT.AuthClient = authClient;
    return authClient;
  };

  public static publicOrganizationSettings = async (options: {
    host: string;
    orgName: string;
  }): Promise<AxiosResponse<PublicOrganizationSettings>> => {
    const { host, orgName } = options;
    if (!host) {
      throw new Error('ServiceHost should be defined');
    }

    if (!host.startsWith('https://api')) {
      throw new Error('ServiceHost must start with "https://api"');
    }

    const axiosInstance = Axios.create({
      headers: {
        'Content-Type': 'application/json',
      },
    });
    const client = new OrganizationClient({ axiosInstance, host, serviceName: 'organization' });
    return await client.getPublicSettings({ orgName });
  };

  public static init = ({ host, apiKey, jwtToken, auth0Token }: ClientOptions): void => {
    if (!host) {
      throw new Error('ServiceHost should be defined');
    }

    if (!host.startsWith('https://api')) {
      throw new Error('ServiceHost must start with "https://api"');
    }

    const authHeader = apiKey
      ? `${apiKey}`
      : jwtToken
      ? `Bearer ${jwtToken}`
      : auth0Token
      ? `auth0 ${auth0Token}`
      : undefined;

    const axiosInstance = Axios.create({
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': authHeader,
      },
    });

    TT.DigitaltwinClient = new DigitaltwinClient({ axiosInstance, host, serviceName: 'digitaltwin' });
    TT.FileClient = new FileClient({ axiosInstance, host, serviceName: 'file' });
    TT.PresentationClient = new PresentationClient({ axiosInstance, host, serviceName: 'presentation' });
    TT.CustodianClient = new CustodianClient({ axiosInstance, host, serviceName: 'custodian' });
    TT.IdentityClient = new IdentityClient({ axiosInstance, host, serviceName: 'identity' });
    TT.VoucherClient = new VoucherClient({ axiosInstance, host, serviceName: 'voucher' });
    TT.TemplateClient = new TemplateClient({ axiosInstance, host, serviceName: 'template' });
    TT.WarrantyClient = new WarrantyClient({ axiosInstance, host, serviceName: 'warranty' });
    TT.TPLOrderClient = new TPLOrderClient({ axiosInstance, host, serviceName: 'tpl-order' });
    TT.OrganizationClient = new OrganizationClient({ axiosInstance, host, serviceName: 'organization' });
    TT.NotificationClient = new NotificationClient({ axiosInstance, host, serviceName: 'notification' });

    TT.axiosInstance = axiosInstance;
  };

  public static enableAuthHandler({
    accessTokenProvider,
    onAccessTokenProviderError: onAccessTokenProviderError,
  }: {
    accessTokenProvider: AccessTokenProvider;
    onAccessTokenProviderError?: OnAccessTokenProviderError;
  }): void {
    TT.axiosInstance.interceptors.response.use(
      httpSuccess => httpSuccess,
      async error => await authInterceptor(error, accessTokenProvider, onAccessTokenProviderError),
    );
  }

  public static setAccessToken({
    apiKey,
    accessToken,
    auth0Token,
  }: {
    apiKey?: string;
    accessToken?: string;
    auth0Token?: string;
  }): void {
    const authHeader = apiKey
      ? `${apiKey}`
      : accessToken
      ? `Bearer ${accessToken}`
      : auth0Token
      ? `auth0 ${auth0Token}`
      : undefined;
    TT.axiosInstance.defaults.headers['x-api-key'] = authHeader;
  }

  private static axiosInstance: AxiosInstance;
}

export type ALL_PERMISSIONS = Array<
  | CUSTODIAN_PERMISSIONS
  | DIGITALTWIN_PERMISSIONS
  | FILE_PERMISSIONS
  | IDENTITY_PERMISSIONS
  | PRESENTATION_PERMISSIONS
  | TEMPLATE_PERMISSIONS
  | VOUCHER_PERMISSIONS
  | ADMIN_PERMISSIONS
  | WARRANTY_PERMISSIONS
  | TPL_ORDER_PERMISSIONS
  | ORGANIZATION_PERMISSIONS
>;

export const ADMIN_PERMISSION_NAMES = { ADMIN_PRIVILEGES: '*:*' };
export type ADMIN_PERMISSIONS = typeof ADMIN_PRIVILEGES;
export const ADMIN_PRIVILEGES = '*:*';

export const ALL_PERMISSION_NAMES = {
  ADMIN: ADMIN_PERMISSION_NAMES,
  DIGITALTWIN: DIGITALTWIN_PERMISSION_NAMES,
  CUSTODIAN: CUSTODIAN_PERMISSION_NAMES,
  PRESENTATION: PRESENTATION_PERMISSION_NAMES,
  IDENTITY: IDENTITY_PERMISSION_NAMES,
  VOUCHER: VOUCHER_PERMISSION_NAMES,
  TEMPLATE: TEMPLATE_PERMISSION_NAMES,
  FILE: FILE_PERMISSION_NAMES,
  WARRANTY: WARRANTY_PERMISSION_NAMES,
  TPL_ORDER: TPL_ORDER_PERMISSION_NAMES,
  ORGANIZATION: ORGANIZATION_PERMISSION_NAMES,
};

export const ALL_PERMISSION_NAMES_ARRAY = Array.prototype.concat(
  ...(Object.values(ALL_PERMISSION_NAMES).map(Object.values) as never[]),
);

export interface ClientOptions {
  host: string;
  apiKey?: string;
  jwtToken?: string;
  auth0Token?: string;
}
